import { App, Button } from 'antd';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { AiClassificationStatusEnum } from '../../enums/AiClassificationStatusEnum';
import { isAiSummaryViewOpenForFile } from '../../components/Ai/aiSummaryViewRegistry';
import { FileOrderColumnEnum } from '../../enums/FilesOrderColumnEnum';
import { WebSocketDataLabelCodeEnum } from '../../enums/WebSocketDataLabelCodeEnum';
import { WebSocketEventTypeEnum } from '../../enums/WebSocketEventTypeEnum';
import type File from '../../interfaces/File';
import type { FileUpdate } from '../../interfaces/File';
import type {
  WebSocketMessage,
  WebSocketMessageDataAiEvent,
} from '../../interfaces/WebSocketMessage';
import type Notification from '../../interfaces/Notification';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import { isDisplayableNotificationEventType } from '../../lib/notifications/displayableNotificationEventTypes';
import {
  coerceAiEventFileIds,
  coerceNotificationFileId,
  resolveAiEventFileRef,
} from '../../lib/notifications/resolveAiEventFileRef';
import {
  resolveNotificationDescription,
  resolveNotificationTitle,
  type NotificationCopySource,
} from '../../lib/notifications/resolveNotificationCopy';
import {
  captureWsSourcePayload,
  logWsNotificationEvent,
} from '../../lib/notifications/wsNotificationDebug';
import { openAiSummaryModal } from '../../utils/openAiSummaryModal';
import { patchFromAntivirusEvent } from '../../utils/antivirus';
import { resolvePdfConversionTerminalErrorMessage } from '../../lib/pdfConversionErrors';
import {
  registerPdfConversionTerminalOutcomeHandler,
  stopPdfConversionPolling,
} from '../../lib/pdfConversionPolling';
import {
  getPendingConversion,
  hasPendingConversion,
  removePendingConversion,
} from '../../lib/pdfConversionTracking';
import {
  buildPdfConversionDestinationPath,
  isPdfFileExtension,
  isViewingPdfConversionDestination,
  resolvePdfConversionDestinationFolderId,
  resolvePdfConversionNewFileId,
  resolvePdfConversionNewFileName,
  type PdfConversionTerminalOutcome,
} from '../../lib/pdfConversionTerminal';

const normalizeWebSocketEventType = (eventType: string): string => eventType.trim().toLowerCase();
const NOTIFIABLE_EVENT_TYPES = new Set<string>([
  WebSocketEventTypeEnum.AiEvent,
  WebSocketEventTypeEnum.SharingOperation,
]);
const NOTIFIABLE_AI_EVENT_SUBTYPES = new Set<string>([
  WebSocketEventTypeEnum.Summary,
  WebSocketEventTypeEnum.Tags,
  WebSocketEventTypeEnum.DocumentClassification,
]);

const TERMINAL_AI_CLASSIFICATION_STATUSES = new Set([
  AiClassificationStatusEnum.Done,
  AiClassificationStatusEnum.Error,
]);

function normalizeAiClassificationStatus(
  status?: WebSocketMessageDataAiEvent['status'],
): AiClassificationStatusEnum | undefined {
  switch (status?.trim().toLowerCase()) {
    case 'queued':
      return AiClassificationStatusEnum.Queued;
    case 'classifying':
      return AiClassificationStatusEnum.Classifying;
    case 'done':
      return AiClassificationStatusEnum.Done;
    case 'error':
      return AiClassificationStatusEnum.Error;
    case 'canceled':
      // Spec: canceled jobs return to None (no classification icon).
      return AiClassificationStatusEnum.None;
    default:
      return undefined;
  }
}

function normalizeBatchStatusToken(batchStatus?: string): string {
  return batchStatus?.trim().toLowerCase() ?? '';
}

/** Spec: batch progress present when total > 0. */
function hasAiBatchProgress(aiEventData: WebSocketMessageDataAiEvent): boolean {
  return typeof aiEventData.total === 'number' && aiEventData.total > 0;
}

function isAiBatchTerminalStatus(batchStatus?: string): boolean {
  const normalized = normalizeBatchStatusToken(batchStatus);
  return normalized === 'success' || normalized === 'failed' || normalized === 'canceled';
}

/**
 * Custom hook for managing notifications from WebSocket events.
 *
 * This hook handles:
 * - Subscribing to WebSocket events
 * - Parsing WebSocket payloads into Notification objects
 * - Batch enrichment of file and user data
 * - Managing notification state through Zustand store
 */
export default function useNotifications() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { modal, message: messageApi, notification: notificationApi } = App.useApp();
  const fileService = useMemo(() => new FileService(), []);

  const notifications = useAppStore((state) => state.notifications);
  const unreadCount = useAppStore((state) => state.unreadCount);
  const addNotification = useAppStore((state) => state.addNotification);
  const updateNotification = useAppStore((state) => state.updateNotification);
  const markAsRead = useAppStore((state) => state.markAsRead);
  const markAllAsRead = useAppStore((state) => state.markAllAsRead);
  const clearAll = useAppStore((state) => state.clearAll);
  const removeNotification = useAppStore((state) => state.removeNotification);
  const enrichInFlightRef = useRef(false);
  /** File ids already requested via getSpecificFiles this session — one shot per id (hotfix /specific). */
  const enrichAttemptedFileIdsRef = useRef<Set<number>>(new Set());

  const notificationNeedsFileEnrichment = useCallback((notification: Notification): boolean => {
    const fileId = coerceNotificationFileId(notification);
    if (fileId == null) {
      return false;
    }
    return (
      !notification.file &&
      !notification.isLoadingFile &&
      !notification.fileEnrichFailed &&
      !enrichAttemptedFileIdsRef.current.has(fileId)
    );
  }, []);

  const markNotificationsFileEnrichFailed = useCallback(
    (fileIds: number[]) => {
      const failedFileIdSet = new Set(fileIds);
      const currentNotifications = useAppStore.getState().notifications ?? [];
      currentNotifications
        .filter((notification) => {
          const fileId = coerceNotificationFileId(notification);
          return fileId != null && failedFileIdSet.has(fileId) && !notification.file;
        })
        .forEach((notification) => {
          updateNotification(notification.id, {
            isLoadingFile: false,
            fileEnrichFailed: true,
          });
        });
    },
    [updateNotification],
  );

  const markNotificationsFileEnrichFailedById = useCallback(
    (notificationIds: string[]) => {
      const failedNotificationIdSet = new Set(notificationIds);
      const currentNotifications = useAppStore.getState().notifications ?? [];
      currentNotifications
        .filter(
          (notification) => failedNotificationIdSet.has(notification.id) && !notification.file,
        )
        .forEach((notification) => {
          updateNotification(notification.id, {
            isLoadingFile: false,
            fileEnrichFailed: true,
          });
        });
    },
    [updateNotification],
  );

  /**
   * Generates a localized title for the notification based on event type.
   * Prefer resolveNotificationTitle at display time for locale switches.
   */
  const generateTitle = useCallback(
    (source: NotificationCopySource): string => resolveNotificationTitle(translate, source),
    [translate],
  );

  /**
   * Generates a localized description for the notification based on event data.
   * Prefer resolveNotificationDescription at display time for locale switches.
   */
  const generateDescription = useCallback(
    (eventData: {
      event_type?: string;
      file_id?: number | string;
      status?: WebSocketMessageDataAiEvent['status'];
      error_message?: string;
      labelCode?: WebSocketDataLabelCodeEnum | string;
      params?: Record<string, string>;
    }): string =>
      resolveNotificationDescription(translate, {
        eventType: eventData.event_type ?? eventData.labelCode ?? '',
        file_id: eventData.file_id,
        labelCode: eventData.labelCode,
        params: eventData.params,
        status: eventData.status,
        errorMessage: eventData.error_message,
      }),
    [translate],
  );

  /**
   * Parses a WebSocketMessage into a Notification object.
   * Branches on event.type: ai_event vs sharing_operation (case-insensitive).
   */
  const parseWebSocketEvent = useCallback(
    (event: WebSocketMessage): Notification => {
      const { id, type, timestamp, data } = event;
      const normalizedEventType = normalizeWebSocketEventType(type);
      const sourcePayload = captureWsSourcePayload(event);

      if (normalizedEventType === WebSocketEventTypeEnum.SharingOperation) {
        const sharingData = data as {
          labelCode: string;
          params?: Record<string, string>;
          fileId?: string | number | null;
        };
        return {
          id,
          timestamp,
          title: generateTitle({ eventType: WebSocketEventTypeEnum.SharingOperation }),
          description: generateDescription({
            labelCode: sharingData.labelCode,
            params: sharingData.params as Record<string, string> | undefined,
          }),
          eventType: WebSocketEventTypeEnum.SharingOperation,
          user_id: 0,
          isRead: false,
          createdAt: new Date().toISOString(),
          labelCode: sharingData.labelCode
            ? normalizeWebSocketEventType(sharingData.labelCode)
            : undefined,
          params: sharingData.params as Record<string, string> | undefined,
          file_id: sharingData.fileId ?? undefined,
          sourcePayload,
        };
      }

      const aiEventData = data as WebSocketMessageDataAiEvent;
      const normalizedAiEventSubtype = normalizeWebSocketEventType(aiEventData.event_type);

      return {
        id,
        timestamp,
        title: generateTitle({
          eventType: normalizedAiEventSubtype,
          status: aiEventData.status,
        }),
        description: generateDescription({
          ...aiEventData,
          event_type: normalizedAiEventSubtype,
          file_id: resolveAiEventFileRef(aiEventData),
        }),
        eventType: normalizedAiEventSubtype,
        user_id: aiEventData.user_id ?? 0,
        file_id: resolveAiEventFileRef(aiEventData),
        isRead: false,
        createdAt: new Date().toISOString(),
        status: aiEventData.status,
        errorMessage: aiEventData.error_message,
        sourcePayload,
      };
    },
    [generateTitle, generateDescription],
  );

  /**
   * Fetches the file and opens the AI summary modal. Used by the "summary ready" toast.
   */
  const openSummaryForFile = useCallback(
    async (fileId: number) => {
      try {
        const response = await fileService.getSpecificFiles([fileId]);
        const file = response.myFiles?.find((candidate) => candidate.id === fileId);
        if (!file) {
          throw new Error(`File ${fileId} not found`);
        }
        openAiSummaryModal({ modal, t: translate, file });
      } catch (error) {
        console.error('Error opening AI summary from notification:', error);
        notificationApi.error({
          title: translate('errors.genericFetchError', {
            resource: translate('common.file'),
          }),
        });
      }
    },
    [fileService, modal, notificationApi, translate],
  );

  /**
   * Shows a toast announcing that an AI summary is ready, with a button to open it.
   * Skipped when a summary view for the file is already open (it updates itself).
   */
  const showSummaryReadyToast = useCallback(
    (eventId: string, fileId: number) => {
      const toastKey = `summary-ready-${eventId}`;
      notificationApi.info({
        key: toastKey,
        title: translate('notifications.summary.title'),
        description: translate('notifications.summary.ready'),
        actions: (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notificationApi.destroy(toastKey);
              void openSummaryForFile(fileId);
            }}
          >
            {translate('notifications.summary.open')}
          </Button>
        ),
      });
    },
    [notificationApi, openSummaryForFile, translate],
  );

  const emitFilesUpdated = useCallback((files: FileUpdate[]) => {
    if (files.length === 0) {
      return;
    }
    useAppStore.getState().events.emit.onFilesUpdated({ files });
  }, []);

  const refreshAiClassificationFile = useCallback(
    async (fileId: number) => {
      try {
        const response = await fileService.getSpecificFiles([fileId]);
        const updatedFile = response.myFiles?.find((candidate) => candidate.id === fileId);

        if (updatedFile) {
          emitFilesUpdated([updatedFile]);
          return;
        }
      } catch (error) {
        console.error('Error refreshing classified file after AI event:', error);
      }

      emitFilesUpdated([
        {
          id: fileId,
          aiOperationStatus: AiClassificationStatusEnum.Done,
          aiOperationErrorMessage: undefined,
        },
      ]);
    },
    [emitFilesUpdated, fileService],
  );

  const handleAntivirusRealtimeUpdate = useCallback(
    (payload: Record<string, unknown>) => {
      const patch = patchFromAntivirusEvent(payload);
      if (!patch) {
        return;
      }

      emitFilesUpdated([
        {
          id: patch.fileId,
          avScanStatus: patch.avScanStatus,
          ...(patch.avThreatName !== undefined ? { avThreatName: patch.avThreatName } : {}),
          ...(patch.avScanError !== undefined ? { avScanError: patch.avScanError } : {}),
        },
      ]);
    },
    [emitFilesUpdated],
  );

  const handleAiClassificationRealtimeUpdate = useCallback(
    (aiEventData: WebSocketMessageDataAiEvent) => {
      const normalizedAiEventSubtype = normalizeWebSocketEventType(aiEventData.event_type);
      if (normalizedAiEventSubtype !== WebSocketEventTypeEnum.Tags) {
        return;
      }

      const resolvedStatus =
        normalizeAiClassificationStatus(aiEventData.status) ?? AiClassificationStatusEnum.Done;
      const fileIds = coerceAiEventFileIds(aiEventData);
      const primaryFileId = fileIds.length === 1 ? fileIds[0] : undefined;

      if (
        typeof aiEventData.batch_id === 'number' &&
        aiEventData.batch_id > 0 &&
        typeof aiEventData.processed === 'number' &&
        typeof aiEventData.total === 'number' &&
        aiEventData.total > 0
      ) {
        useAppStore.getState().events.emit.batchProgressUpdated({
          batchId: aiEventData.batch_id,
          processed: aiEventData.processed,
          total: aiEventData.total,
          pending: aiEventData.pending,
          processing: aiEventData.processing,
          succeeded: aiEventData.succeeded,
          failed: aiEventData.failed,
          canceled: aiEventData.canceled,
          eventTs: aiEventData.event_ts,
          batchStatus:
            typeof aiEventData.batch_status === 'string' ? aiEventData.batch_status : undefined,
          ...(primaryFileId != null ? { fileId: primaryFileId } : {}),
          ...(typeof aiEventData.file_name === 'string' ? { fileName: aiEventData.file_name } : {}),
          ...(typeof aiEventData.status === 'string' ? { fileStatus: aiEventData.status } : {}),
        });
      }

      // Full REST refresh on terminal/legacy events; mid-batch progress comes from push only.
      if (
        !hasAiBatchProgress(aiEventData) ||
        isAiBatchTerminalStatus(aiEventData.batch_status) ||
        resolvedStatus === AiClassificationStatusEnum.Queued ||
        resolvedStatus === AiClassificationStatusEnum.None
      ) {
        useAppStore.getState().events.emit.refreshBatches();
      }

      if (fileIds.length === 0) {
        return;
      }

      if (resolvedStatus === AiClassificationStatusEnum.Done) {
        emitFilesUpdated(
          fileIds.map((fileId) => ({
            id: fileId,
            aiOperationStatus: AiClassificationStatusEnum.Done,
            aiOperationErrorMessage: undefined,
          })),
        );
        fileIds.forEach((fileId) => {
          void refreshAiClassificationFile(fileId);
        });
        return;
      }

      emitFilesUpdated(
        fileIds.map((fileId) => ({
          id: fileId,
          aiOperationStatus: resolvedStatus,
          aiOperationErrorMessage:
            resolvedStatus === AiClassificationStatusEnum.Error
              ? aiEventData.error_message || aiEventData.error_code
              : undefined,
        })),
      );
    },
    [emitFilesUpdated, refreshAiClassificationFile],
  );

  /**
   * Resolve converted PDF: prefer getSpecificFiles by id; if BE omits id, search
   * destination by `newFileName` (newest ModifiedAt).
   */
  const resolveConvertedPdfFile = useCallback(
    async (
      newFileId: number | null | undefined,
      destinationFolderId?: number | null,
      newFileName?: string,
      sourceFileName?: string,
      sourceFileId?: number | null,
    ): Promise<File | null> => {
      if (
        newFileId != null &&
        newFileId > 0 &&
        (sourceFileId == null || Number(newFileId) !== Number(sourceFileId))
      ) {
        const response = await fileService.getSpecificFiles([newFileId]);
        const created = response.myFiles?.find(
          (candidate) => Number(candidate.id) === Number(newFileId),
        );
        // Only accept a real PDF — BE may echo the source id on success payloads.
        if (created && !created.isFolder && isPdfFileExtension(created.fileExtension)) {
          return created;
        }
      }

      const searchBase = (newFileName || sourceFileName || '')
        .replace(/\.pdf$/i, '')
        .replace(/\.[^.]+$/, '')
        .trim();
      if (!searchBase) {
        return null;
      }
      // Personal root: omit `parent` (same as file list / move picker). Folder: pass id.
      const response = await fileService.getFiles({
        type: 2,
        list: 0,
        offset: 0,
        count: 20,
        ...(destinationFolderId != null && destinationFolderId > 0
          ? { parent: String(destinationFolderId) }
          : {}),
        search: searchBase,
        orderColumn: FileOrderColumnEnum.ModifiedAt,
        orderAsc: false,
      });
      const candidates = response.myFiles ?? [];
      // Newest PDF whose title starts with the source/new base (handles "name(1).pdf").
      const match = candidates.find(
        (f) =>
          !f.isFolder &&
          isPdfFileExtension(f.fileExtension) &&
          (sourceFileId == null || Number(f.id) !== Number(sourceFileId)) &&
          (f.fileName === searchBase ||
            f.fileName?.startsWith(`${searchBase}(`) ||
            f.fileName?.startsWith(searchBase)),
      );
      return match ?? null;
    },
    [fileService],
  );

  /**
   * Pin converted PDF into open folder (upload / Office-create path).
   * Same folder → `prependFileId` URL; otherwise emit `onFilesCreated`.
   */
  const refreshConvertedPdfInList = useCallback(
    async (outcome: {
      newFileId?: number | null;
      destinationFolderId?: number | null;
      newFileName?: string;
      sourceFileName?: string;
      sourceFileId?: number | null;
    }) => {
      try {
        const created = await resolveConvertedPdfFile(
          outcome.newFileId,
          outcome.destinationFolderId,
          outcome.newFileName,
          outcome.sourceFileName,
          outcome.sourceFileId,
        );
        const resolvedId =
          created?.id ??
          (outcome.newFileId != null &&
          outcome.newFileId > 0 &&
          (outcome.sourceFileId == null ||
            Number(outcome.newFileId) !== Number(outcome.sourceFileId))
            ? outcome.newFileId
            : null);

        const destinationPath = buildPdfConversionDestinationPath(
          outcome.destinationFolderId,
          resolvedId,
        );
        const onDestinationFolder = isViewingPdfConversionDestination(
          location.pathname,
          outcome.destinationFolderId,
        );

        // Viewing dest (incl. personal root `/files/personal`): always pin via URL.
        if (onDestinationFolder && resolvedId != null) {
          navigate(destinationPath, { replace: true });
          return created ? { ...created, isUnRead: true } : null;
        }

        if (!created) {
          return null;
        }

        const withParent =
          created.parentId == null &&
          outcome.destinationFolderId != null &&
          outcome.destinationFolderId > 0
            ? { ...created, parentId: outcome.destinationFolderId, isUnRead: true }
            : { ...created, isUnRead: true };

        useAppStore.getState().events.emit.onFilesCreated({ files: [withParent] });
        return withParent;
      } catch (error) {
        console.error('Error refreshing converted PDF in file list:', error);
        return null;
      }
    },
    [location.pathname, navigate, resolveConvertedPdfFile],
  );

  const handlePdfConversionTerminalOutcome = useCallback(
    (outcome: PdfConversionTerminalOutcome) => {
      if (outcome.kind === 'timeout') {
        notificationApi.info({
          title: translate('notifications.pdfConversionStillRunning'),
        });
        return;
      }

      if (outcome.kind === 'success') {
        void refreshConvertedPdfInList(outcome).then((created) => {
          const destinationPath = buildPdfConversionDestinationPath(
            outcome.destinationFolderId,
            created?.id ?? outcome.newFileId,
          );
          // Already on destination: file prepended in list — no "Open folder" action.
          const onDestinationFolder = isViewingPdfConversionDestination(
            location.pathname,
            outcome.destinationFolderId,
          );
          const successText = translate('notifications.pdfConversionSuccess');
          const fileLabel = outcome.newFileName ?? created?.fileName;

          if (onDestinationFolder) {
            // message has no `actions` API (antd); plain toast is ideal when no nav needed.
            messageApi.success({
              content: fileLabel ? `${successText} ${fileLabel}` : successText,
            });
            return;
          }

          // Tradeoff: message supports ReactNode `content` (and whole-toast onClick), not
          // notification-style `actions`. Embed a link button for navigate-away UX.
          messageApi.success({
            content: (
              <span>
                {fileLabel ? `${successText} ${fileLabel}` : successText}{' '}
                <Button type="link" size="small" onClick={() => navigate(destinationPath)}>
                  {translate('common.openFolder')}
                </Button>
              </span>
            ),
            duration: 5,
          });
        });
        return;
      }

      const errorMessage = resolvePdfConversionTerminalErrorMessage(
        outcome.errorCode ?? outcome.errorMessage,
        translate,
      );
      notificationApi.error({
        title: translate('notifications.pdfConversionFailed'),
        description: errorMessage,
      });
    },
    [
      location.pathname,
      messageApi,
      navigate,
      notificationApi,
      refreshConvertedPdfInList,
      translate,
    ],
  );

  useEffect(() => {
    return registerPdfConversionTerminalOutcomeHandler(handlePdfConversionTerminalOutcome);
  }, [handlePdfConversionTerminalOutcome]);

  /**
   * Handles incoming WebSocket events and creates notifications.
   * Deduplicates by event.id. Supports sharing_operation and ai_event (case-insensitive).
   */
  const handleWebSocketEvent = useCallback(
    (event: WebSocketMessage) => {
      if (!event?.id || !event?.type) return;

      const normalizedEventType = normalizeWebSocketEventType(event.type);

      // PDF Conversion events
      if (normalizedEventType === WebSocketEventTypeEnum.FileEvent) {
        const fileEventPayload = (event.data ?? {}) as Record<string, unknown>;
        const eventType = normalizeWebSocketEventType(String(fileEventPayload.event_type ?? ''));
        if (eventType === WebSocketEventTypeEnum.PdfConversion) {
          const processId = String(fileEventPayload.process_id ?? fileEventPayload.processId ?? '');
          const status = String(fileEventPayload.status ?? '').toLowerCase();
          const newFileName = resolvePdfConversionNewFileName(fileEventPayload);
          const errorCode = String(fileEventPayload.error_code ?? fileEventPayload.errorCode ?? '');

          // Check if we're tracking this conversion
          if (!hasPendingConversion(processId)) {
            return;
          }

          const pending = getPendingConversion(processId);
          const destinationFolderId = resolvePdfConversionDestinationFolderId(
            fileEventPayload,
            pending?.destinationFolderId,
          );

          removePendingConversion(processId);
          stopPdfConversionPolling(processId);

          if (status === 'success') {
            handlePdfConversionTerminalOutcome({
              kind: 'success',
              processId,
              newFileName: newFileName || undefined,
              sourceFileName: pending?.sourceFileName,
              sourceFileId: pending?.fileId,
              newFileId: resolvePdfConversionNewFileId(fileEventPayload, pending?.fileId),
              destinationFolderId,
            });
          } else if (status === 'failed') {
            handlePdfConversionTerminalOutcome({
              kind: 'failed',
              processId,
              errorCode: errorCode || undefined,
            });
          }
        }
        return;
      }

      if (normalizedEventType === WebSocketEventTypeEnum.AntivirusEvent) {
        const antivirusPayload = (event.data ?? {}) as Record<string, unknown>;
        const antivirusSubtype = normalizeWebSocketEventType(
          String(antivirusPayload.event_type ?? ''),
        );
        if (antivirusSubtype === WebSocketEventTypeEnum.AntivirusScan) {
          handleAntivirusRealtimeUpdate(antivirusPayload);
        }
        return;
      }

      if (!NOTIFIABLE_EVENT_TYPES.has(normalizedEventType)) return;

      logWsNotificationEvent(event, { action: 'create' });

      const existingNotifications = useAppStore.getState().notifications ?? [];
      if (existingNotifications.some((notification) => notification.id === event.id)) {
        logWsNotificationEvent(event, { action: 'skip', reason: 'duplicate_event_id' });
        return;
      }

      if (normalizedEventType === WebSocketEventTypeEnum.SharingOperation) {
        try {
          const notification = parseWebSocketEvent(event);
          addNotification(notification);
        } catch (error) {
          console.error('Error parsing SharingOperation for notification:', error);
        }
        return;
      }

      if (normalizedEventType === WebSocketEventTypeEnum.AiEvent) {
        const aiEventData = event.data as WebSocketMessageDataAiEvent;
        const aiEventSubtype = aiEventData?.event_type;
        const normalizedAiEventSubtype = aiEventSubtype
          ? normalizeWebSocketEventType(aiEventSubtype)
          : null;

        if (normalizedAiEventSubtype == null || normalizedAiEventSubtype === '') {
          logWsNotificationEvent(event, { action: 'skip', reason: 'missing_ai_event_subtype' });
          return;
        }
        if (!NOTIFIABLE_AI_EVENT_SUBTYPES.has(normalizedAiEventSubtype)) {
          logWsNotificationEvent(event, { action: 'skip', reason: 'unsupported_ai_event_subtype' });
          return;
        }

        handleAiClassificationRealtimeUpdate(aiEventData);

        if (normalizedAiEventSubtype === WebSocketEventTypeEnum.Tags) {
          const resolvedStatus =
            normalizeAiClassificationStatus(aiEventData.status) ?? AiClassificationStatusEnum.Done;

          // Batch progress present: one notification when multi-file batch finishes, not per file.
          // Skip toast on cancel mid-flight / canceled batch end (user-initiated).
          if (hasAiBatchProgress(aiEventData)) {
            const batchStatus = normalizeBatchStatusToken(aiEventData.batch_status);
            if (batchStatus === 'canceled') {
              logWsNotificationEvent(event, { action: 'skip', reason: 'batch_canceled' });
              return;
            }
            if (!isAiBatchTerminalStatus(aiEventData.batch_status)) {
              logWsNotificationEvent(event, { action: 'skip', reason: 'batch_in_progress' });
              return;
            }

            const total = aiEventData.total ?? 0;
            if (total > 1) {
              const batchId = aiEventData.batch_id ?? 0;
              const batchNotificationId = `ai-tags-batch-${batchId}-${batchStatus}`;
              const existing = useAppStore.getState().notifications ?? [];
              if (existing.some((notification) => notification.id === batchNotificationId)) {
                logWsNotificationEvent(event, {
                  action: 'skip',
                  reason: 'duplicate_batch_notification',
                });
                return;
              }

              const processed = aiEventData.processed ?? 0;
              const isFailed = batchStatus === 'failed';
              const batchFileId = resolveAiEventFileRef(aiEventData);
              addNotification({
                id: batchNotificationId,
                timestamp: event.timestamp,
                title: translate(
                  isFailed
                    ? 'notifications.tags.batchFailedTitle'
                    : 'notifications.tags.batchSuccessTitle',
                ),
                description: translate(
                  isFailed
                    ? 'notifications.tags.batchFailedDescription'
                    : 'notifications.tags.batchSuccessDescription',
                  { processed, total },
                ),
                eventType: WebSocketEventTypeEnum.Tags,
                user_id: aiEventData.user_id ?? 0,
                ...(batchFileId != null ? { file_id: batchFileId } : {}),
                batch_id: batchId,
                batchProcessed: processed,
                batchTotal: total,
                isRead: false,
                createdAt: new Date().toISOString(),
                status: isFailed ? 'error' : 'done',
                errorMessage: isFailed ? aiEventData.error_message : undefined,
                sourcePayload: captureWsSourcePayload(event),
              });
              return;
            }
            // total <= 1: fall through to single-file tags notification.
          }

          if (!TERMINAL_AI_CLASSIFICATION_STATUSES.has(resolvedStatus)) {
            logWsNotificationEvent(event, { action: 'skip', reason: 'non_terminal_tags_status' });
            return;
          }
        }

        try {
          const notification = parseWebSocketEvent(event);
          addNotification(notification);
        } catch (error) {
          console.error('Error parsing WebSocket event for notification:', error);
        }

        // Announce a ready summary with a toast, unless its view is already open.
        if (normalizedAiEventSubtype === WebSocketEventTypeEnum.Summary) {
          const summaryFileId = resolveAiEventFileRef(aiEventData);
          const numericSummaryFileId =
            typeof summaryFileId === 'number'
              ? summaryFileId
              : typeof summaryFileId === 'string'
                ? Number(summaryFileId)
                : undefined;
          if (
            typeof numericSummaryFileId === 'number' &&
            Number.isFinite(numericSummaryFileId) &&
            !isAiSummaryViewOpenForFile(numericSummaryFileId)
          ) {
            showSummaryReadyToast(event.id, numericSummaryFileId);
          }
        }
      }
    },
    [
      parseWebSocketEvent,
      addNotification,
      handleAiClassificationRealtimeUpdate,
      handleAntivirusRealtimeUpdate,
      showSummaryReadyToast,
      translate,
      handlePdfConversionTerminalOutcome,
    ],
  );

  /**
   * Enriches notifications with file data by batching file ID requests (one shot per file_id).
   */
  const enrichNotificationsWithFiles = useCallback(async () => {
    if (enrichInFlightRef.current) {
      return;
    }

    const currentNotifications = useAppStore.getState().notifications ?? [];
    const notificationsNeedingFiles = currentNotifications.filter(notificationNeedsFileEnrichment);

    if (notificationsNeedingFiles.length === 0) {
      return;
    }

    const uniqueFileIds: number[] = [
      ...new Set(
        notificationsNeedingFiles
          .map((notification) => coerceNotificationFileId(notification))
          .filter((fileId): fileId is number => fileId != null),
      ),
    ];

    if (uniqueFileIds.length === 0) {
      return;
    }

    enrichInFlightRef.current = true;
    uniqueFileIds.forEach((fileId) => enrichAttemptedFileIdsRef.current.add(fileId));

    const notificationIdsNeedingFiles = notificationsNeedingFiles.map(
      (notification) => notification.id,
    );

    notificationsNeedingFiles.forEach((notification) => {
      updateNotification(notification.id, { isLoadingFile: true });
    });

    try {
      const response = await fileService.getSpecificFiles(uniqueFileIds);
      const returnedFiles = response.myFiles ?? [];
      const returnedFileIdSet = new Set(returnedFiles.map((file) => file.id));

      returnedFiles.forEach((file) => {
        const notificationsForFile = (useAppStore.getState().notifications ?? []).filter(
          (notification) => coerceNotificationFileId(notification) === file.id,
        );
        notificationsForFile.forEach((notification) => {
          updateNotification(notification.id, {
            file,
            isLoadingFile: false,
            fileEnrichFailed: false,
          });
        });
      });

      const missingFileIds = uniqueFileIds.filter((fileId) => !returnedFileIdSet.has(fileId));
      if (missingFileIds.length > 0) {
        markNotificationsFileEnrichFailed(missingFileIds);
      }
    } catch (error) {
      console.error('Error enriching notifications with file data:', error);
      markNotificationsFileEnrichFailed(uniqueFileIds);
      markNotificationsFileEnrichFailedById(notificationIdsNeedingFiles);
    } finally {
      enrichInFlightRef.current = false;
    }
  }, [
    fileService,
    markNotificationsFileEnrichFailed,
    markNotificationsFileEnrichFailedById,
    notificationNeedsFileEnrichment,
    updateNotification,
  ]);

  useEffect(() => {
    const unsubscribeWebSocketEvent = useAppStore
      .getState()
      .events.on.webSocketEvent(handleWebSocketEvent);

    return unsubscribeWebSocketEvent;
  }, [handleWebSocketEvent]);

  useEffect(() => {
    const needsEnrichment = notifications.some(notificationNeedsFileEnrichment);
    if (!needsEnrichment) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void enrichNotificationsWithFiles();
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [notifications, enrichNotificationsWithFiles, notificationNeedsFileEnrichment]);

  const sortedNotifications = useMemo(() => {
    if (!Array.isArray(notifications)) {
      return [];
    }
    return [...notifications]
      .filter((notification) => isDisplayableNotificationEventType(notification.eventType))
      .sort(
        (firstNotification, secondNotification) =>
          new Date(secondNotification.createdAt).getTime() -
          new Date(firstNotification.createdAt).getTime(),
      );
  }, [notifications]);

  return {
    notifications: sortedNotifications,
    unreadCount: typeof unreadCount === 'number' ? unreadCount : 0,
    markAsRead,
    markAllAsRead,
    clearAll,
    removeNotification,
  };
}
